"use client"

import { CommentType } from "@/app/types/comment"
import { PaginationType } from "@/app/types/pagination"
import { ProductDetailType } from "@/app/types/product-site"
import { Avatar, Box, Button, colors, Divider, Fade, IconButton, NoSsr, Skeleton, Typography } from "@mui/material"
import Logo from "@v2/components/Logo"
import MediaV2 from "@v2/components/MediaV2"
import FlexBox from "@v2/components/styled/FlexBox"
import api from "@v2/lib/api"
import axios, { PaginatedAxiosResponse } from "@v2/lib/axios"
import { uiColors } from "@v2/lib/color"
import dayjs from "@v2/lib/dayjs"
import { useAppSelector } from "@v2/lib/redux/hooks"
import { AxiosError } from "axios"
import { ChevronDown, Heart, MessageSquare, ThumbsUp, User } from "lucide-react"
import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { useSnackbar } from "notistack"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import PdpAddCommentButton from "../partials/PdpAddCommentButton"

export default function PdpComments({
    product,
    siteProductId
}: {
    product: ProductDetailType,
    siteProductId: number
}) {

    const session = useAppSelector(s => s.general.session)
    const isAuth = session?.isAuthenticated ?? false;
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const currentPath = pathname + (searchParams.size > 0 ? `?${searchParams.toString()}` : '')
    const signInHref = `/fa/sign-in?callback_url=${encodeURIComponent(`${currentPath}#comments`)}`
    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        getComments()
    }, [])

    const [loading, setLoading] = useState(true)
    const [comments, setComments] = useState<CommentType[]>([])
    const [pagination, setPagination] = useState<PaginationType>()
    const getComments = useCallback((page = 1) => {

        if (page === 1) {
            setLoading(true)
        }
        axios.get(api.comments(siteProductId) + `?limit=4&page=${page}`).then((res: PaginatedAxiosResponse) => {
            setLoading(false)
            setComments(page === 1 ? res.data.data : [...comments, ...res.data.data])
            setPagination(res.data.pagination)
        }).catch((err: AxiosError) => {
            setLoading(false)

        })
    }, [comments])

    const [tempLikes, setTempLikes] = useState<{ [key in number]: boolean }>({})

    const toggleLike = useCallback((comment_id, changeTo) => () => {

        if (!isAuth) return enqueueSnackbar({
            message: (
                <Box>
                    <Typography sx={{ fontSize: 14 }}>برای لایک ابتدا وارد حساب کاربری شود</Typography>
                    <Button color="inherit" variant="outlined" component={Link} prefetch={false} href={signInHref} sx={{
                        mt: 1, minWidth: 68
                    }}>ورود</Button>
                </Box>
            )
        })

        setTempLikes(tmp => {

            return { ...tmp, [comment_id]: changeTo }
        })

        axios.post(api.like_comment, {
            comment_id
        })
    }, [isAuth, signInHref])

    const loadMoreComments = useCallback(() => {
        getComments(pagination.current_page + 1)
    }, [pagination])

    const calcFromNow = useMemo(() => (datetime: string) => {
        return dayjs(datetime, 'YYYY-MM-DD HH:mm:ss').locale('fa').fromNow();
    }, [])

    return (
        <NoSsr fallback={<PdpCommentsLoadingSkeleton />}>
            <Box sx={{
                bgcolor: '#fff',
                p: 2,
                mt: '4px',
                borderRadius: 1,
            }}>
                <FlexBox sx={{
                    justifyContent: 'space-between',
                    mb: 2,
                    minHeight: 36
                }}>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MessageSquare size={20} color={colors.grey[700]} />
                        <Typography id="comments" sx={{
                            fontSize: 18, fontWeight: 500,
                            color: colors.grey[800],
                        }}>دیدگاه کاربران
                            {(!!pagination?.total && pagination?.total > 0) && (
                                <Typography component="span" sx={{ ml: 1, fontSize: 13 }}>
                                    ({pagination.total})
                                </Typography>
                            )}
                        </Typography>
                    </Box>

                    <PdpAddCommentButton product={product} siteProductId={siteProductId} />

                </FlexBox>

                <Box>
                    {loading ? <LoadingSkeleton /> : comments.length > 0 ? (
                        <Box>
                            {comments.map((comment, index) => {
                                const isLiked = tempLikes[comment.id] ?? comment.is_liked
                                return (
                                    <FlexBox key={comment.id} sx={{
                                        alignItems: 'flex-start',
                                        mb: 1,
                                        gap: 1,
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}>
                                        <Avatar sx={{ bgcolor: colors.grey[200], position: 'relative', zIndex: 2, outline: '4px solid #fff' }}>
                                            {comment.user?.avatar ? (<MediaV2 media_id={comment.user.avatar.id} alt="user_profile" width={40} height={40} />) : (
                                                <User />
                                            )}
                                        </Avatar>
                                        <Box sx={{ flex: 1, }}>

                                            <FlexBox sx={{ mt: 1, }}>
                                                <Typography component="span" sx={{
                                                    fontSize: '16px !important',
                                                    fontWeight: 500,
                                                    color: colors.grey[900]

                                                }}>{comment.is_unknown ? 'کاربر ناشناس' : (comment.user.first_name || "کاربر")}</Typography>
                                                <Divider flexItem orientation="vertical" sx={{ mx: 1, my: 0.5 }} />
                                                <Typography component="span" sx={{
                                                    fontSize: 13,
                                                    fontWeight: 400,
                                                    color: colors.grey[600]
                                                }}>{calcFromNow(comment.created_at)}</Typography>
                                            </FlexBox>
                                            <Typography sx={{
                                                fontSize: 15,
                                                color: colors.grey[800],
                                                textAlign: 'justify',
                                                mt: 2,
                                                whiteSpace: 'pre-line'
                                            }}>
                                                {comment.description}
                                            </Typography>

                                            <FlexBox sx={{
                                                justifyContent: 'space-between',
                                                mt: 1
                                            }}>
                                                {comment.tags ? (
                                                    <Box sx={{
                                                        bgcolor: colors.grey[200],
                                                        py: 0.5,
                                                        px: 1,
                                                        fontSize: 13,
                                                        borderRadius: 1,
                                                        color: colors.grey[700]
                                                    }}>
                                                        {comment.tags?.join('  •  ')}
                                                    </Box>
                                                ) : (
                                                    <Box />
                                                )}
                                                <FlexBox>
                                                    {comment.likes > 0 && (
                                                        <Fade in={true}>
                                                            <Typography sx={{
                                                                mr: 1,
                                                                fontSize: 13,
                                                                color: colors.grey[700]
                                                            }}>{comment.likes}</Typography>
                                                        </Fade>
                                                    )}
                                                    <IconButton onClick={toggleLike(comment.id, !isLiked)} sx={{
                                                        bgcolor: '#fff',
                                                        height: 38, width: 38
                                                    }} aria-label="like or dislike comment">
                                                        {isLiked ? (
                                                            <Heart fill={colors.red[700]} color={colors.red[700]} />
                                                        ) : (
                                                            <Heart color={colors.grey[700]} />
                                                        )}
                                                    </IconButton>
                                                </FlexBox>
                                            </FlexBox>

                                            {comment.children.map((child, index2) => {
                                                const childIsLiked = tempLikes[child.id] ?? child.is_liked

                                                return (
                                                    <FlexBox key={child.id} sx={{
                                                        alignItems: 'stretch',
                                                        gap: 1,
                                                        mt: 1,
                                                    }}>
                                                        <Box sx={{
                                                            position: 'relative',
                                                            height: 40,
                                                            '&::after': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                left: -30,
                                                                // top: 'calc(-50% + 1px)',
                                                                bottom: '50%',
                                                                height: '240px',
                                                                width: 30,
                                                                borderLeft: '1px solid',
                                                                borderBottom: '1px solid',

                                                                borderColor: colors.grey[200],
                                                                borderRadius: '0 0 0 8px',
                                                            }
                                                        }}>
                                                            <Avatar sx={{
                                                                bgcolor: colors.grey[200],
                                                                position: 'relative',
                                                                zIndex: 1,
                                                                outline: '4px solid #fff'
                                                            }}>
                                                                {child.is_admin ? (
                                                                    <Logo type="micro" />
                                                                    // <MicroLogoSrc width={24} height={30}/>
                                                                    // <Image src={microLogoSrc} alt="vahdat admin" height={24} width={24} />
                                                                ) : child.user?.avatar ? (<MediaV2 media_id={child.user.avatar.id} alt="user_profile" height={40} width={40} />) : (
                                                                    <User />
                                                                )}
                                                            </Avatar>
                                                        </Box>
                                                        <Box sx={{
                                                            flex: 1
                                                        }}>

                                                            <FlexBox sx={{
                                                                mt: 1,
                                                            }}>

                                                                <Typography component="span" sx={{
                                                                    fontSize: '16px !important',
                                                                    fontWeight: 500,
                                                                    color: colors.grey[900]

                                                                }}>{child.is_unknown ? 'کاربر' : (child.is_admin ? "ادمین سایت" : child.user.title_name)}</Typography>
                                                                <Divider flexItem orientation="vertical" sx={{ mx: 1, my: 0.5 }} />
                                                                <Typography component="span" sx={{
                                                                    fontSize: 13,
                                                                    fontWeight: 400,
                                                                    color: colors.grey[600]
                                                                }}>{calcFromNow(child.created_at)}</Typography>
                                                            </FlexBox>
                                                            <Typography sx={{
                                                                fontSize: 15,
                                                                color: colors.grey[800],
                                                                textAlign: 'justify',
                                                                mt: 2,
                                                                whiteSpace: 'pre-line',
                                                                lineHeight: '27px'
                                                            }}>
                                                                {child.description}
                                                            </Typography>
                                                        </Box>
                                                        <FlexBox sx={{
                                                            flexDirection: 'column',
                                                            justifyContent: 'flex-end',
                                                        }}>
                                                            <FlexBox>
                                                                {child.likes > 0 && (
                                                                    <Fade in={true}>
                                                                        <Typography sx={{
                                                                            mr: 1,
                                                                            fontSize: 13,
                                                                            color: colors.grey[700]
                                                                        }}>{child.likes}</Typography>
                                                                    </Fade>
                                                                )}
                                                                <IconButton onClick={toggleLike(child.id, !childIsLiked)} sx={{
                                                                    bgcolor: '#fff',
                                                                    height: 38, width: 38
                                                                }} aria-label="like or dislike comment">
                                                                    {childIsLiked ? (
                                                                        <ThumbsUp color={uiColors.primary} />
                                                                    ) : (
                                                                        <ThumbsUp color={colors.grey[700]} />
                                                                    )}
                                                                </IconButton>
                                                            </FlexBox>
                                                        </FlexBox>
                                                    </FlexBox>
                                                )
                                            })}
                                            {comments.length - 1 !== index && (
                                                <Divider flexItem sx={{ my: 2 }} />
                                            )}
                                        </Box>

                                    </FlexBox>
                                )
                            })}

                            {pagination?.total > 3 && ((pagination?.total ?? 0) > comments.length) && (
                                <FlexBox sx={{
                                    justifyContent: 'center'
                                }}>
                                    <Button color="inherit"
                                        onClick={loadMoreComments}
                                        sx={{
                                            height: 48,
                                            minWidth: 200
                                        }}
                                        variant="outlined"
                                        endIcon={<ChevronDown />}>نمایش دیدگاه های بیشتر</Button>
                                </FlexBox>
                            )}
                        </Box>
                    ) : (


                        <FlexBox sx={{
                            textAlign: 'center',
                            // mt: 2,
                            height: 150,
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <Typography sx={{
                                fontSize: {
                                    xs: 13, sm: 14, md: 15
                                }, color: colors.grey[700], mt: 2
                            }}>
                                تا کنون هیچ دیدگاهی برای این کالا ثبت نشده است.
                            </Typography>
                            <Typography sx={{
                                fontSize: { xs: 14, sm: 15, md: 16 }, color: colors.grey[800], fontWeight: 500, mt: 2
                            }}>
                                شما اولین نفر باشید
                            </Typography>
                        </FlexBox>
                    )}
                </Box>
            </Box>
        </NoSsr>
    )
}

export const PdpCommentsLoadingSkeleton = memo(() => (
    <Box sx={{
        p: 2,
        bgcolor: '#fff',
        mt: '4px',
        borderRadius: 1,
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 2 }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={120} height={28} />
        </Box>
        <Skeleton variant="rounded" width="100%" height={150} />
    </Box>
))

const LoadingSkeleton = memo(() => (
    <Box>
        <Skeleton variant="rounded" width="100%" height={150} />
    </Box>
))
